import React from "react";
import { useSelector } from "react-redux";
import { ShieldOff } from "lucide-react";
import { RootState } from "../../redux/store";
import { hasAccess, selectModuleGrants } from "../../redux/selectors/authSelectors";
import { AccessControl } from "../../redux/actions/authActions";
import { menuIdsForFlag } from "../../utils/landingRoute";

interface RequireAccessProps {
  /** Permission flag from the login response that gates this route. */
  flag?: keyof AccessControl;
  /**
   * Sidebar id this route belongs to, for routes no role flag covers (ABHA,
   * Calendar, User History). Only enforced when a per-user grid applies.
   */
  module?: string;
  children: React.ReactNode;
}

/**
 * Route-level permission gate.
 *
 * The sidebar hides menu items the user has no permission for, but hiding a link
 * is not access control — before this existed, typing the address of a gated
 * screen loaded it in full. This closes that hole in the UI.
 *
 * IMPORTANT: this is a client-side check only. The permission flags arrive with
 * the login response and are readable (and editable) in the browser, so the API
 * must enforce the same rules per request. This component stops accidental and
 * casual access; it is not a security boundary on its own.
 *
 * Renders an explanation rather than redirecting, so a denied user lands
 * somewhere intelligible instead of being bounced around the app.
 */
export default function RequireAccess({ flag, module, children }: RequireAccessProps) {
  const grants = useSelector(selectModuleGrants);
  const flagAllowed = useSelector((state: RootState) => (flag ? hasAccess(state, flag) : true));

  /*
   * With a per-user grid from Settings → Authorization, the grid decides for
   * every route that maps to a sidebar entry — the role flag only for the ones
   * that do not (team management, for one). Without a grid, the flag decides.
   */
  const modules = module ? [module] : flag ? menuIdsForFlag(flag) : [];
  const allowed =
    grants && modules.length > 0
      ? modules.some((id) => grants.includes(id))
      : flagAllowed;

  if (!allowed) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <ShieldOff className="mx-auto mb-4 h-10 w-10 text-gray-400" aria-hidden="true" />
          <h1 className="mb-2 text-lg font-semibold text-gray-900">
            You don't have access to this page
          </h1>
          <p className="text-sm text-gray-600">
            Your account doesn't include permission for this area. If you think you
            should have it, ask an administrator to update your access.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
